import { checkmarkCircleOutline, closeOutline } from "ionicons/icons";
import { type Application } from "@/lib/backend/api";

export type ApplicationStatus = Application["status"];

type StatusDisplay = {
  /** Key in the `enums` namespace. The word the employee reads. */
  labelKey: string;
  icon: string;
  /** Tailwind text colour for the icon and the label. */
  color: string;
};

/**
 * One application status, in the employee's own language.
 *
 * The two cards that show a status each carried their own `switch`, with the Dutch word written
 * straight into the `label`. Nothing was missing from the bundles and no key failed to resolve, so
 * neither the parity gate nor the key gate could see it: an English interface simply said
 * "Afgewezen" where it should say "Declined", and a rejected application was the one state an
 * English-speaking employee could not read (Boomer-Software/app#174).
 *
 * The icon and the colour live here too, because that is what made the duplication tempting in the
 * first place — a status that gained a colour in one card and not in the other.
 *
 * A status the api adds later has no entry here. The callers fall back to the raw value rather than
 * render an empty chip, which is what the old `default:` branch did.
 */
export const APPLICATION_STATUS_DISPLAY: Record<ApplicationStatus, StatusDisplay> = {
  PENDING: {
    labelKey: "enums:applicationCardStatus.PENDING",
    icon: checkmarkCircleOutline,
    color: "text-brand-500",
  },
  ACCEPTED: {
    labelKey: "enums:applicationCardStatus.ACCEPTED",
    icon: checkmarkCircleOutline,
    color: "text-success-120",
  },
  REJECTED: {
    labelKey: "enums:applicationCardStatus.REJECTED",
    icon: checkmarkCircleOutline,
    color: "text-error-120",
  },
  RESERVE: {
    labelKey: "enums:applicationCardStatus.RESERVE",
    icon: checkmarkCircleOutline,
    color: "text-warning-120",
  },
  CONSUMED: {
    labelKey: "enums:applicationCardStatus.CONSUMED",
    icon: checkmarkCircleOutline,
    color: "text-success-120",
  },
  CANCELLATION_REQUESTED: {
    labelKey: "enums:applicationCardStatus.CANCELLATION_REQUESTED",
    icon: checkmarkCircleOutline,
    color: "text-warning-120",
  },
  REVOCATION_REQUESTED: {
    labelKey: "enums:applicationCardStatus.REVOCATION_REQUESTED",
    icon: checkmarkCircleOutline,
    color: "text-warning-120",
  },
  CANCELLED: {
    labelKey: "enums:applicationCardStatus.CANCELLED",
    icon: closeOutline,
    color: "text-txt-tertiary",
  },
};

/** The icon and colour a status the api does not know about falls back to. */
export const UNKNOWN_STATUS_DISPLAY = {
  icon: checkmarkCircleOutline,
  color: "text-txt-secondary",
} as const;
